/**
 * @fileoverview SSO (Single Sign-On) service utilities for Microsoft authentication.
 * Provides functions to check SSO availability, initiate Microsoft login, and handle authentication callbacks.
 */

import { fetchData, intl } from "dmsbasereact";
import { getBaseURL } from  "../build_config";

/**
 * Gets the current SSO status and configuration from the server.
 *
 * @returns {Promise<Object>} Object containing SSO status data and HTTP status code
 * @returns {Object} return.data - SSO configuration data or null if unavailable
 * @returns {number} return.status - HTTP status code
 * @returns {Error} [return.error] - Error object if request fails
 *
 * @example
 * const { data, status } = await getSSOStatus();
 * if (status === 200 && data) {
 *   console.log('SSO is configured');
 * }
 */
export const getSSOStatus = async () => {
    try {
        const response = await fetchData("/login/microsoft/status", "GET", null, false);
        if (response.status === 200) {
            return { data: response.data.data || response.data, status: response.status };
        }
        return { data: null, status: response.status };
    } catch (error) {
        console.error("Error getting SSO status:", error);
        return { data: null, status: 500, error };
    }
};

/**
 * Initiates Microsoft SSO login by opening an authentication popup window.
 *
 * @returns {Window} Reference to the opened popup window
 *
 * @example
 * const popup = initiateMicrosoftLogin();
 * // Listen for popup close or message events to handle authentication result
 */
export const initiateMicrosoftLogin = () => {
    const popupWindow = window.open(
      `${getBaseURL()}/login/microsoft/login`,
      'SSOLogin Lena',
      'width=600,height=600'
    );
    popupWindow.document.title = intl.get('SSOLoginPopTitle').d("SSO Login Lena");
    return popupWindow;
};

/**
 * Handles the Microsoft authentication callback after user completes SSO login.
 * Processes the authorization code or error from the OAuth flow.
 *
 * @param {URLSearchParams} searchParams - URL search parameters from the OAuth callback
 * @returns {Promise<Object>} Authentication result object
 * @returns {boolean} return.success - Whether authentication was successful
 * @returns {Object} [return.data] - Authentication data including access_token if successful
 * @returns {string} [return.error] - Error message if authentication failed
 *
 * @example
 * const searchParams = new URLSearchParams(window.location.search);
 * const result = await handleMicrosoftCallback(searchParams);
 * if (result.success) {
 *   // Store tokens and redirect to app
 *   console.log('Access token:', result.data.access_token);
 * } else {
 *   console.error('Authentication failed:', result.error);
 * }
 */
export const handleMicrosoftCallback = async (searchParams) => {
    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (error) {
        return {
            success: false,
            error: errorDescription || error,
        };
    }

    if (!code) {
        return {
            success: false,
            error: "Authorization code not found",
        };
    }

    try {
        const response = await fetchData(`/login/microsoft/callback?code=${encodeURIComponent(code)}`, "GET", null, false);

        if (response.status >= 200 && response.status < 300 && response.data.access_token) {
            return {
                success: true,
                data: response.data,
            };
        } else {
            return {
                success: false,
                error: response.data.detail || "Authentication failed",
            };
        }
    } catch (error) {
        console.error("Error handling Microsoft callback:", error);
        return {
            success: false,
            error: "Failed to complete authentication",
        };
    }
};
